import { Stack, Text } from "@mantine/core"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, Missing } from "@tfg/ui"
import { useMemo } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { useActivitiesContext } from "~/components/contexts/useActivitiesContext"
import { useOrganizationsContext } from "~/pages/Admin/MasterLists/CustomFields/contexts/useOrganizationsContext"

const columnHelper = createColumnHelper<AdminResponses.CustomFieldDefinition>()
export const useColumns = (
  handleDelete: (id: number) => void,
  handleUpdate: (customField: AdminResponses.CustomFieldDefinition) => void
) => {
  const activities = useActivitiesContext()
  const organizations = useOrganizationsContext()

  return useMemo<ColumnDef<AdminResponses.CustomFieldDefinition, any>[]>(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor(
        (row) => {
          const activity = activities.find(
            (activity) => activity.id === row.forActivityId
          )
          return activity?.name
        },
        {
          header: "For Activity",
          cell: ({ getValue }) => <Text>{getValue()}</Text>,
        }
      ),
      columnHelper.accessor("enabledForOrganizationsIds", {
        header: "Organizations",
        cell: ({ getValue }) => {
          const enabledOrganizations = organizations.filter((organization) =>
            getValue().includes(organization.id)
          )
          return enabledOrganizations.length > 0 ? (
            <Text>
              {enabledOrganizations
                .map((organization) => organization.name)
                .join(", ")}
            </Text>
          ) : (
            <Missing />
          )
        },
      }),
      columnHelper.accessor("textContent.originalText", {
        header: "Original Text",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor(
        (row) => {
          const translation = row.textContent.TextTranslation.find(
            ({ translatedLanguage }) => translatedLanguage === "EN"
          )
          return translation?.translation ?? ""
        },
        {
          header: "EN Translation",
          cell: ({ getValue }) => <Text>{getValue()}</Text>,
        }
      ),
      columnHelper.accessor(
        (row) => {
          const translation = row.textContent.TextTranslation.find(
            ({ translatedLanguage }) => translatedLanguage === "FR"
          )
          return translation?.translation ?? ""
        },
        {
          header: "FR Translation",
          cell: ({ getValue }) => <Text>{getValue()}</Text>,
        }
      ),
      columnHelper.accessor("constraint", {
        header: "Constraint",
        cell: ({ getValue }) => <Text>{getValue()}</Text>,
      }),
      columnHelper.accessor("enumValuesOriginalText", {
        header: "Enum Values",
        cell: ({ getValue }) => (
          <Stack>
            {getValue().map((enumValue: { originalText: string }) => (
              <Text key={enumValue.originalText}>{enumValue.originalText}</Text>
            ))}
          </Stack>
        ),
      }),
      columnHelper.accessor((row) => ({ min: row.min, max: row.max }), {
        header: "Min-Max",
        cell: ({ getValue }) => {
          const { min, max } = getValue()

          return min === null && max === null ? (
            <Missing />
          ) : (
            <Text>{`${min ?? ""}-${max ?? ""}`}</Text>
          )
        },
      }),

      columnHelper.accessor("message", {
        header: "Message",
        cell: ({ getValue }) => (
          <Text>{getValue()?.originalText ?? <Missing />}</Text>
        ),
      }),
      columnHelper.accessor("onActivityId", {
        header: "On Activity",
        cell: ({ getValue }) => {
          const activity = activities.find(
            (activity) => activity.id === getValue()
          )
          return activity?.name
        },
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "delete",
        cell: ({ row }) => (
          <ActionIcon
            type="delete"
            onClick={() => handleDelete(row.original.id)}
          />
        ),
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "update",
        cell: ({ row }) => (
          <ActionIcon type="edit" onClick={() => handleUpdate(row.original)} />
        ),
      }),
    ],
    [handleDelete, handleUpdate, activities, organizations]
  )
}
